import { Injectable, signal, computed } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import type { Confirmation, ConfirmationState } from './confirm-dialog.types';

/**
 * Opaque identifier for a confirmation issued via {@link ConfirmDynamicDialogService.confirm}.
 * Callers hold it ONLY to pass back into {@link ConfirmDynamicDialogService.dismiss}
 * for scoped dismissal — no meaning may be attached to its value.
 */
export type ConfirmationId = number;

/**
 * Confirmation Service - Razor UI Design System
 * Drop-in replacement for ConfirmationService
 *
 * Concurrency semantics: single visible dialog + FIFO queue. A confirm()
 * issued while a dialog is already open is QUEUED and shown after the
 * current dialog resolves (accept / reject / close), so every dialog
 * resolves with its OWN accept/reject callbacks. Previously a second
 * confirm() overwrote the stored confirmation, silently dropping the
 * on-screen dialog's callbacks and firing the new dialog's callbacks in
 * their place. FIFO was chosen after a 2026-07-31 call-site survey
 * (~60 components, 277 references): every caller passes one-shot decision
 * callbacks and none re-calls confirm() to mutate an open dialog, so
 * deferring the second dialog is the least-surprising sound behavior.
 *
 * Programmatic dismissal MUST be scoped: `confirm()` returns a
 * {@link ConfirmationId} and `dismiss(id)` retires exactly that dialog —
 * closing it if it is the visible one, or silently removing it from the
 * queue otherwise. With FIFO queueing a bare `close()` means "dismiss
 * whatever is visible + promote next", which lets a caller kill an
 * UNRELATED dialog (dropping its callbacks) and later resurface its own
 * stale queued dialog. `close()` is therefore reserved for the dialog
 * COMPONENT's internal resolution paths (accept / reject / mask click),
 * where "the visible dialog" is by definition the one being resolved.
 *
 * @example
 * // In component
 * private confirmService = inject(ConfirmDynamicDialogService);
 *
 * deleteItem() {
 *   this.confirmService.confirm({
 *     message: 'Are you sure you want to delete this item?',
 *     header: 'Confirm Delete',
 *     icon: 'exclamation-triangle',
 *     accept: () => {
 *       // Delete logic
 *     },
 *     reject: () => {
 *       // Cancel logic
 *     }
 *   });
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class ConfirmDynamicDialogService {
  private _state = signal<ConfirmationState>({
    visible: false,
    confirmation: null
  });

  /** Confirmations waiting for the current dialog to resolve (FIFO). */
  private readonly _queue: { id: ConfirmationId; confirmation: Confirmation }[] = [];

  /** Monotonic id source for {@link confirm}. */
  private _nextId: ConfirmationId = 0;

  /** Id of the currently visible confirmation (null when idle). */
  private _visibleId: ConfirmationId | null = null;

  private _requireConfirmation$ = new Subject<Confirmation>();
  private _accept$ = new Subject<void>();
  private _reject$ = new Subject<void>();
  private _close$ = new Subject<void>();

  /** Current confirmation state */
  readonly state = computed(() => this._state());

  /** Whether dialog is visible */
  readonly visible = computed(() => this._state().visible);

  /** Current confirmation request */
  readonly confirmation = computed(() => this._state().confirmation);

  /** Observable for confirmation requests */
  readonly requireConfirmation$: Observable<Confirmation> = this._requireConfirmation$.asObservable();

  /** Observable for accept events */
  readonly accept$: Observable<void> = this._accept$.asObservable();

  /** Observable for reject events */
  readonly reject$: Observable<void> = this._reject$.asObservable();

  /** Observable for close events */
  readonly close$: Observable<void> = this._close$.asObservable();

  /**
   * Request confirmation - compatible with ConfirmationService.confirm()
   *
   * If a dialog is already open the request is queued (FIFO) and shown
   * after the current dialog resolves — see the class doc for rationale.
   * `requireConfirmation$` emits when a confirmation is actually shown,
   * not when it is queued.
   *
   * Returns an opaque {@link ConfirmationId} identifying THIS request.
   * Callers that may need to retire their dialog programmatically (e.g.
   * a countdown that fires before the operator decides) must keep it and
   * call {@link dismiss} with it — never bare {@link close}, which acts
   * on whichever dialog happens to be visible.
   *
   * Reentrancy: confirm() called synchronously from inside an accept/reject
   * callback is safe — the current dialog is still marked visible at that
   * point, so the new request queues and is then shown by the close() that
   * accept()/reject() run after the callback returns.
   */
  confirm(confirmation: Confirmation): ConfirmationId {
    const id = ++this._nextId;
    if (this._state().visible) {
      this._queue.push({ id, confirmation });
      return id;
    }
    this._visibleId = id;
    this._state.set({
      visible: true,
      confirmation
    });
    this._requireConfirmation$.next(confirmation);
    return id;
  }

  /**
   * Accept the confirmation
   */
  accept(): void {
    const confirmation = this._state().confirmation;
    if (confirmation?.accept) {
      confirmation.accept();
    }
    this._accept$.next();
    this.close();
  }

  /**
   * Reject/cancel the confirmation
   */
  reject(): void {
    const confirmation = this._state().confirmation;
    if (confirmation?.reject) {
      confirmation.reject();
    }
    this._reject$.next();
    this.close();
  }

  /**
   * Close the current dialog without triggering its accept/reject, then
   * show the next queued confirmation (if any). This is the single
   * queue-advance point — accept() and reject() both funnel through it,
   * as does mask dismissal.
   *
   * RESERVED for the dialog component's internal resolution paths
   * (accept / reject / mask click), where the visible dialog is by
   * definition the one being resolved. Any OTHER caller that wants to
   * retire a dialog it opened must use {@link dismiss} with the id its
   * confirm() returned — a bare close() from outside the component can
   * dismiss an unrelated visible dialog (silently dropping its
   * callbacks) and later promote the caller's stale queued dialog.
   */
  close(): void {
    const next = this._queue.shift();
    this._visibleId = next ? next.id : null;
    this._state.set(
      next
        ? { visible: true, confirmation: next.confirmation }
        : { visible: false, confirmation: null }
    );
    this._close$.next();
    if (next) {
      this._requireConfirmation$.next(next.confirmation);
    }
  }

  /**
   * Scoped programmatic dismissal of ONE confirmation, identified by the
   * id its {@link confirm} call returned:
   *
   * - If that confirmation is currently VISIBLE, it is closed without
   *   firing its accept/reject and the next queued confirmation (if any)
   *   is promoted — exactly like a mask dismissal of that dialog.
   * - If it is still QUEUED, it is removed from the queue silently; the
   *   visible dialog (which belongs to someone else) is left untouched.
   * - If it has already resolved (accepted / rejected / closed) — or the
   *   id is unknown — this is a no-op. In particular it can never close
   *   a LATER dialog that happens to be on screen.
   */
  dismiss(id: ConfirmationId): void {
    if (this._visibleId === id) {
      this.close();
      return;
    }
    const queueIndex = this._queue.findIndex(entry => entry.id === id);
    if (queueIndex !== -1) {
      this._queue.splice(queueIndex, 1);
    }
    // Already resolved or unknown → no-op.
  }

  /**
   * Check if dialog is currently visible
   */
  isVisible(): boolean {
    return this._state().visible;
  }
}

// Export as alias for backward compatibility
export { ConfirmDynamicDialogService as ConfirmationService };
